/**
 * Watermark sizing and placement arithmetic, shared by the editor preview and
 * the burn-in worker (VE-24693).
 *
 * ⚠️ THIS MODULE MUST NEVER IMPORT ANYTHING. `burnInWorker.ts` and
 * `mupdfRenderWorker.ts` deliberately carry zero static imports so nothing can
 * drag the DOM, redux or a bundle's worth of app code into a worker. This file
 * is importable from a worker only for as long as it stays a leaf. If you need
 * something from `models.ts` here, you need a different file.
 *
 * It also owns how a text mark's LINES are read (`watermarkTextLines`), how
 * tall the resulting block is, and where an ANCHORED mark's centre falls
 * (`anchoredCenterPt`), for the same reason: the overlay lays the block
 * out in CSS and the worker lays it out in PDF content ops, so the line cap and
 * the line height are two more numbers the two sides must not reach
 * independently.
 *
 * `anchoredCenterPt` serves the Bates stamp (VE-24447) as well as watermarks,
 * which is why it takes optional per-axis insets: a stamp may sit a different
 * distance from the sides than from the header/footer, and a watermark never
 * does. A Bates-only copy of this arithmetic is precisely the drift described
 * below, so the axis split lives here rather than in a second routine.
 *
 * It exists because the preview and the export have to agree on the size and
 * angle of a mark, and they used to agree only by having the same arithmetic
 * typed out twice. They drifted: the mean glyph advance was 0.5 in three places
 * and 0.55 in a fourth, the `scale` fallback was 0.4 on one side and 0.6 on the
 * other, and the preview clamped `scale` into [0.02, 1] while the worker did
 * not — so a mark could export at twice its previewed size. A watermark whose
 * on-screen position is the operator's only defence against covering evidence
 * (AC#7, U4-FR11) cannot be sized by two functions that merely look alike.
 */

/**
 * Mean glyph advance for Helvetica, in em. Used to convert between a text
 * width and a font size in both directions. Approximate by nature — real
 * advance depends on the string — but it must be the SAME approximation
 * everywhere or the preview and the export disagree.
 */
export const TEXT_ADVANCE_EM = 0.5;

/** Legibility floor for a derived font size, in PDF points. */
export const TEXT_FONT_MIN_PT = 6;

/** Ceiling for a stored font size, in PDF points. */
export const TEXT_FONT_MAX_PT = 400;

/**
 * Step for the text-size slider. The control is UNITLESS — a coefficient has
 * nothing to be wrong about, whereas a "24pt" readout is only true for one page
 * size — so the step exists to give the slider a sane number of positions
 * across [MIN_FONT_SCALE, MAX_FONT_SCALE] rather than to name a unit.
 */
export const FONT_SCALE_STEP = 0.005;

/**
 * Size of a newly applied IMAGE mark, as a share of page width.
 *
 * The fallback, not the policy: a library definition carries its own `size`
 * default and this is what a mark gets when it has none — an ad-hoc watermark,
 * or a definition saved before the field existed.
 */
export const DEFAULT_WATERMARK_SCALE = 0.5;

/**
 * Size of a newly applied TEXT mark, as a coefficient of PAGE WIDTH applied to
 * the FONT SIZE: `fontSizePt = fontScale x pageWidthPt`.
 *
 * 0.04 is about 24pt on US Letter. It is deliberately not the "50 characters
 * across 80% of the page" figure from the first pass, which came out smaller in
 * practice than intended.
 *
 * The coefficient is the point of the model, and it took three tries to get
 * here. The original stored a fraction of page width for the WHOLE STRING and
 * back-derived the font size by dividing by character count, so the apparent
 * type size tracked how much text there was: at one setting "OK" rendered at
 * 306pt and a 34-character line at 18pt. Storing points instead fixed that and
 * gave up the property the fraction was chosen for — a fixed 20pt is a banner
 * on A5 and a lost line on A0. A coefficient on the font size is independent of
 * BOTH: the string plays no part, and it scales with the page.
 */
export const DEFAULT_FONT_SCALE = 0.04;

/**
 * Range of the text-size SLIDER — about 6pt to 122pt on Letter.
 *
 * A control range, not a validity range, and the difference is load-bearing.
 * What actually bounds a text mark is the rendered size, clamped to
 * [TEXT_FONT_MIN_PT, TEXT_FONT_MAX_PT] against the page it lands on. Clamping
 * the COEFFICIENT as well would be a second limit on the same quantity, which
 * is the mistake the old model made — and it would silently shrink converted
 * legacy marks, because the old model could express coefficients up to 2.0 (a
 * one-character mark at full page width) that these bounds cannot.
 */
export const MIN_FONT_SCALE = 0.01;
export const MAX_FONT_SCALE = 0.2;

/**
 * Share of the page width a text mark may occupy before it is shrunk to fit.
 *
 * A CEILING, not a scale: `fontScale` decides the size, and this only ever
 * reduces it, and only when the rendered string would run off the page. Nothing
 * about it depends on the string in the ordinary case, which is exactly the
 * property the old character-count division destroyed.
 */
export const TEXT_OVERFLOW_LIMIT = 0.9;

/** Smallest size a mark may be given — below this it is ungrabbable on canvas. */
export const MIN_WATERMARK_SCALE = 0.02;

/**
 * Coerce a stored `scale` into the legal range. Applied on BOTH sides: an
 * out-of-range value reaching only one of them is precisely how the export came
 * to differ from the preview.
 */
export function clampWatermarkScale(scale: number | undefined | null): number {
  if (typeof scale !== 'number' || !Number.isFinite(scale)) {
    return DEFAULT_WATERMARK_SCALE;
  }
  return Math.max(MIN_WATERMARK_SCALE, Math.min(scale, 1));
}

/**
 * Most lines a text watermark may carry.
 *
 * A watermark is a stamp, not a paragraph: past three lines it stops being
 * something the eye takes in at once and starts competing with the document it
 * sits on. The video text-overlay tool caps at two for the same reason.
 */
export const MAX_WATERMARK_TEXT_LINES = 3;

/**
 * Baseline-to-baseline distance, in ems of the font size.
 *
 * 1.2 is the typographic default and what a browser uses for `normal`, which
 * matters here beyond taste: the overlay lays the block out in CSS and the
 * worker lays it out in PDF content ops, so a number either side could reach by
 * a different route is a number they can disagree about.
 */
export const TEXT_LINE_HEIGHT_EM = 1.2;

/**
 * The lines of a watermark, as everything that draws one must read them.
 *
 * Stored as ONE `\n`-joined string rather than numbered fields, matching the
 * video text-overlay tool — numbered fields make every consumer know how many
 * there are, and admit states like "line 3 set, line 2 empty" that a string
 * cannot express.
 *
 * Capped here rather than at each caller, so a value written by another client
 * or a future build cannot make the preview and the export disagree about how
 * much of it to draw. Always returns at least one entry: a mark with no text is
 * an empty line, not zero lines, which keeps the height arithmetic total.
 */
export function watermarkTextLines(text: string): string[] {
  const lines = (text ?? '').split(/\r?\n/).slice(0, MAX_WATERMARK_TEXT_LINES);
  return lines.length > 0 ? lines : [''];
}

/**
 * The largest coefficient that still RENDERS larger, for this text.
 *
 * `textFontSizePt` shrinks a mark that would run past
 * {@link TEXT_OVERFLOW_LIMIT} of the page width, so past this point the
 * coefficient keeps climbing and the mark stops growing.
 *
 * The page width CANCELS, which is what makes this a property of the TEXT
 * alone rather than of the page it lands on:
 *
 *     maxSizePt = LIMIT x W / (ADVANCE x len)
 *     maxScale  = maxSizePt / W = LIMIT / (ADVANCE x len)
 *
 * A CEILING, never a control's range. It was once the text-size slider's `max`,
 * and that is what VE-30051 was: the ceiling rises as the longest line gets
 * shorter, a slider draws its thumb at `value / max`, so deleting a character
 * slid the thumb backward with nothing stored changing. A control whose extent
 * depends on the content cannot report a stable position, which is why the text
 * size is stepped rather than dragged. Apply this to the VALUE — see
 * {@link fitFontScale} — not to a range.
 */
export function maxFontScaleFor(text: string): number {
  const len = Math.max(
    1,
    watermarkTextLines(text).reduce(
      (max, line) => Math.max(max, line.length),
      0
    )
  );
  const overflowBound = TEXT_OVERFLOW_LIMIT / (TEXT_ADVANCE_EM * len);
  // Never below the floor: a very long line would otherwise bound the slider
  // under its own minimum and leave a control with no usable travel at all.
  return Math.max(MIN_FONT_SCALE, Math.min(MAX_FONT_SCALE, overflowBound));
}

/**
 * A font scale this TEXT can actually show, inside the control's own range.
 *
 * THE one place the "cap it to what fits" rule lives. It used to be written out
 * inline wherever someone remembered it, and the site that was missed is the
 * one that mattered: the control's DISPLAYED value. A mark resized on canvas
 * could hold a coefficient the page never rendered, so the number on screen
 * named a size the operator was not looking at.
 *
 * Both bounds, in order: {@link clampFontScale} for the control's range and
 * non-finite input, then the text's own ceiling. Never below
 * {@link MIN_FONT_SCALE}, because `maxFontScaleFor` is floored there too.
 */
export function fitFontScale(
  scale: number | undefined | null,
  text: string
): number {
  return Math.min(clampFontScale(scale), maxFontScaleFor(text));
}

/**
 * The size range, IN WHOLE POINTS, the text-size control offers on a page this
 * wide — the CONTROL's own range, which has nothing to do with the text.
 *
 * Deliberately not narrowed by {@link maxFontScaleFor}. A control whose extent
 * depends on the content cannot report a stable position, and narrowing it is
 * what VE-30051 was. The text's limit is shown beside the number instead — see
 * {@link textFontSizeCapPt} — so the thing that moves when the text changes is
 * visibly the LIMIT, not the operator's setting.
 *
 * Rounded INWARD, so every integer the control offers is one the renderer
 * honours.
 */
export function fontSizeRangePt(nativeWidthPt: number): {
  readonly minPt: number;
  readonly maxPt: number;
} {
  const minPt = Math.ceil(
    Math.max(TEXT_FONT_MIN_PT, MIN_FONT_SCALE * nativeWidthPt)
  );
  const maxPt = Math.floor(
    Math.min(TEXT_FONT_MAX_PT, MAX_FONT_SCALE * nativeWidthPt)
  );
  return { minPt, maxPt: Math.max(minPt, maxPt) };
}

/**
 * The largest size THIS TEXT prints at on a page this wide, in whole points.
 *
 * {@link maxFontScaleFor} resolved against a page and rounded down, so it can
 * be compared with the number in the control. A setting above it is not an
 * error — the mark simply prints at this size instead — so it is REPORTED, not
 * enforced: the control hands the size over to a Fit control and keeps the
 * setting, greyed, so shortening the text gives it straight back. That is what
 * makes the mark growing again as the operator backspaces something they were
 * told about in advance rather than a surprise.
 */
export function textFontSizeCapPt(text: string, nativeWidthPt: number): number {
  const { minPt, maxPt } = fontSizeRangePt(nativeWidthPt);
  return Math.max(
    minPt,
    Math.min(maxPt, Math.floor(maxFontScaleFor(text) * nativeWidthPt))
  );
}

/**
 * Bound a stored watermark text: at most {@link MAX_WATERMARK_TEXT_LINES}
 * lines, each at most `maxPerLine` characters.
 *
 * PER LINE, which is the whole point. Both read boundaries used to
 * `slice(0, 50)` the entire string, which was right while a mark was one line
 * and silently eats lines 2 and 3 of a three-line one — a truncation that
 * happens on READ, so the operator's own saved watermark comes back short with
 * nothing said.
 *
 * `maxPerLine` is passed in rather than imported: this module is the burn-in
 * worker's only static import and must stay a leaf, and the limit belongs to
 * `documents/models` with the rest of the product caps.
 */
export function clampWatermarkText(text: string, maxPerLine: number): string {
  return watermarkTextLines(text)
    .map((line) => line.slice(0, maxPerLine))
    .join('\n');
}

/**
 * Width of the drawn block at `fontSize`, in PDF points — the LONGEST line.
 *
 * The block is centred, so its width is its widest line and the shorter ones
 * sit inside it. Sizing on the total character count instead would make a
 * three-line mark shrink to a third of a one-line mark saying the same thing
 * per line, which is the string-dependence this model exists to avoid.
 */
export function textWidthPt(text: string, fontSize: number): number {
  const longest = watermarkTextLines(text).reduce(
    (max, line) => Math.max(max, line.length),
    0
  );
  return Math.max(longest * fontSize * TEXT_ADVANCE_EM, fontSize);
}

/**
 * Height of the drawn block at `fontSize`, in PDF points.
 *
 * `(n - 1)` line gaps plus one line's own height, so a single line measures
 * exactly `fontSize` and the block grows by a full line height for each one
 * after it. Used by the overlay for its hit box and by the worker to place the
 * first baseline, which is why it lives here and not in either of them.
 */
export function textBlockHeightPt(text: string, fontSize: number): number {
  const count = watermarkTextLines(text).length;
  return fontSize * (1 + (count - 1) * TEXT_LINE_HEIGHT_EM);
}

/**
 * Coerce a stored text coefficient into the legal range.
 *
 * The counterpart of {@link clampWatermarkScale}, and the ONLY clamp a text
 * mark passes through in the ordinary case. It used to pass through two —
 * `scale` into [0.02, 1] and the derived point size into [6, 400] — two limits
 * fighting over one quantity. They disagreed, and `resizePatch` records what
 * that cost: a value clamped on the way in, clamped again differently on the
 * way out, and a resize handle that came loose at the ends.
 */
export function clampFontScale(scale: number | undefined | null): number {
  if (typeof scale !== 'number' || !Number.isFinite(scale)) {
    return DEFAULT_FONT_SCALE;
  }
  return Math.max(MIN_FONT_SCALE, Math.min(scale, MAX_FONT_SCALE));
}

/**
 * The coefficient of a text mark. THE reader — every caller goes through this,
 * so the legacy conversion lives in exactly one place.
 *
 * A mark written before this change stores `scale`, a fraction of page width
 * for the whole string. Converting is exact and needs no reference page:
 *
 *     oldFontPt = scale x W / (ADVANCE x len)
 *     fontScale = oldFontPt / W = scale / (ADVANCE x len)
 *
 * The page width cancels, so the COEFFICIENT is exact on every page size — not
 * an approximation resolved against Letter. That is worth having, because
 * "nothing has shipped so there are no old rows" was believed about
 * `imageDataUrl` in this same feature and the development registry had rows.
 *
 * One deliberate difference in the RENDERED result, and it is not a conversion
 * error: {@link TEXT_OVERFLOW_LIMIT} is new, so a legacy mark that used to run
 * the full width of the page is now reduced to 90% of it. That is the behaviour
 * the ceiling was added for, and it applies to old and new marks alike.
 */
export function fontScaleOf(wm: {
  readonly text: string;
  readonly fontScale?: number | null;
  readonly scale?: number | null;
}): number {
  // Deliberately NOT put through `clampFontScale`. That is the slider's range,
  // and applying it here would be a second bound on a quantity the rendered
  // size already bounds — the exact shape of the bug this model replaced. It
  // would also break the conversion below: the old model could express
  // coefficients up to 2.0, so a legacy mark would come back visibly smaller
  // than it always rendered. Only finiteness and sign are enforced here;
  // `textFontSizePt` does the bounding, in points, against the actual page.
  const usable = (n: unknown): n is number =>
    typeof n === 'number' && Number.isFinite(n) && n > 0;

  if (usable(wm.fontScale)) {
    return wm.fontScale;
  }
  if (usable(wm.scale)) {
    // The LONGEST line, which for a legacy mark (never multi-line) is the whole
    // string — so the conversion stays exact for the rows it exists for.
    const len = Math.max(1, longestLineLength(wm.text));
    // `clampWatermarkScale` first, because the OLD model applied it before
    // dividing. Without it the conversion is not exact at the bottom of the
    // range: a legacy `scale` of 0.005 rendered at the 0.02 floor and would
    // come back a quarter of the size. The old floor is part of what the old
    // value MEANT.
    return clampWatermarkScale(wm.scale) / (TEXT_ADVANCE_EM * len);
  }
  return DEFAULT_FONT_SCALE;
}

/**
 * The legacy `scale` a text mark must carry so an OLDER client renders it right.
 *
 * The pre-split worker computed `fontSize = scale x W / (ADVANCE x len)`, so the
 * value that makes it arrive at this mark's actual size is
 * `fontScale x ADVANCE x len`. Inverting rather than copying matters: a text
 * mark's own `scale` is a frozen default that means nothing, and writing THAT
 * is how a rolled-back client would render every text watermark at half the
 * page width.
 *
 * Only for the burn-in payload and the persisted asset. Nothing current reads
 * it — {@link fontScaleOf} prefers `fontScale` whenever it is present.
 */
export function legacyTextScale(wm: {
  readonly text: string;
  readonly fontScale?: number | null;
  readonly scale?: number | null;
}): number {
  const len = Math.max(1, longestLineLength(wm.text));
  return clampWatermarkScale(fontScaleOf(wm) * TEXT_ADVANCE_EM * len);
}

/** Characters in the widest line — what the block's width is measured from. */
function longestLineLength(text: string): number {
  return watermarkTextLines(text).reduce(
    (max, line) => Math.max(max, line.length),
    0
  );
}

/**
 * Font size of a text mark on a page this wide, in PDF points.
 *
 * `fontScale x pageWidthPt`, then shrunk ONLY if the rendered string would run
 * past {@link TEXT_OVERFLOW_LIMIT} of the width. The shrink is a ceiling, not a
 * scale — a mark that fits is untouched however long it is, which is what makes
 * the size independent of the string.
 */
export function textFontSizePt(
  wm: {
    readonly text: string;
    readonly fontScale?: number | null;
    readonly scale?: number | null;
  },
  nativeWidthPt: number
): number {
  const size = fontScaleOf(wm) * nativeWidthPt;
  const maxWidth = TEXT_OVERFLOW_LIMIT * nativeWidthPt;
  const width = textWidthPt(wm.text, size);
  const fitted = width > maxWidth ? size * (maxWidth / width) : size;
  return Math.max(TEXT_FONT_MIN_PT, Math.min(fitted, TEXT_FONT_MAX_PT));
}

/**
 * Coerce a stored rotation into the range the editor's slider offers.
 *
 * Lives in this leaf, not in `watermarks/models`, because the consumer that
 * needed it most could not reach it there: `documents/helpers` reads applied
 * marks back from a saved asset, and importing the watermarks module from the
 * documents module would be the wrong direction. Its siblings —
 * `clampWatermarkScale`, `clampFontScale`, `fontScaleOf` — are all here for
 * the same reason, and so the burn-in worker can use them too.
 *
 * -90..90, not the full circle: a mark at 135 degrees is a mark at -45 upside
 * down and a watermark reads the same either way, so the other half is
 * duplicate settings. Values outside it are wrapped rather than clamped — an
 * old mark at 135 should come back as the -45 it always looked like, not
 * flattened to the nearest end.
 */
export function clampWatermarkRotation(deg: number | undefined | null): number {
  if (typeof deg !== 'number' || !Number.isFinite(deg)) {
    return 0;
  }
  let wrapped = ((deg % 360) + 360) % 360;
  if (wrapped > 180) {
    wrapped -= 360;
  }
  if (wrapped > 90) {
    wrapped -= 180;
  }
  if (wrapped < -90) {
    wrapped += 180;
  }
  return wrapped;
}

/**
 * Where an unplaced mark sits on the page — the "Position on Screen" grid,
 * borrowed from the video text-overlay tool so the two features pin things the
 * same way.
 *
 * `center`/`middle` is the historical behaviour and stays the default: every
 * mark written before this existed reads as centred.
 */
export type WatermarkAnchorH = 'left' | 'center' | 'right';

/**
 * How the LINES of a multi-line text mark sit against each other.
 *
 * Distinct from {@link WatermarkAnchorH}, which is where the whole block sits
 * on the page — a block pinned bottom-right can still have its lines centred
 * within it. Absent reads as `center`, which is how every multi-line mark was
 * drawn before the control existed.
 *
 * Meaningless on a single line: the block is exactly as wide as its only line,
 * so there is nothing to align within. The UI disables it there rather than
 * offering a control that cannot do anything.
 */
export type WatermarkTextAlign = 'left' | 'center' | 'right';

/**
 * X offset of one line inside the block, in PDF points, relative to the block's
 * LEFT edge.
 *
 * The worker positions each line with this and the overlay lets CSS
 * `text-align` do it — different mechanisms, one rule, which is why the rule
 * lives here rather than being expressed twice.
 */
export function lineOffsetPt(
  align: WatermarkTextAlign | undefined | null,
  lineWidthPt: number,
  blockWidthPt: number
): number {
  const slack = Math.max(0, blockWidthPt - lineWidthPt);
  if (align === 'left') {
    return 0;
  }
  if (align === 'right') {
    return slack;
  }
  return slack / 2;
}
export type WatermarkAnchorV = 'top' | 'middle' | 'bottom';

/**
 * "Inset from edge", as a fraction of the SMALLER page dimension.
 *
 * Same values and the same reasoning as `TEXT_OVERLAY_INSET_PRESETS`: a
 * fraction of the smaller dimension keeps the gap looking equal on a landscape
 * page, where a fraction of the width would be a much bigger margin at the
 * sides than at the top. Redeclared here rather than imported because this
 * module is the burn-in worker's only static import and has to stay a leaf.
 *
 * Only edge anchors use it — an inset from centre means nothing, and applying
 * one would move a centred mark off centre for no reason the operator asked
 * for.
 */
export const WATERMARK_INSET_PRESETS = {
  none: 0,
  small: 0.015,
  medium: 0.03,
  large: 0.08,
  xl: 0.16,
} as const;

export const DEFAULT_WATERMARK_INSET = WATERMARK_INSET_PRESETS.medium;

/**
 * Centre of an ANCHORED mark, in PDF points with a TOP-LEFT origin.
 *
 * The one implementation, called by `watermarkBox` for the preview and by the
 * burn-in worker for the export. Both used to hardcode "centre of the page" for
 * an unplaced mark, in their own arithmetic — which was fine while centre was
 * the only option and is exactly the shape that drifts once it is not.
 *
 * The mark's own size is passed in because an edge anchor positions its EDGE:
 * a right-anchored mark's centre depends on how wide it is, so the caller that
 * knows the drawn size has to supply it.
 */
export function anchoredCenterPt(
  wm: {
    readonly anchorH?: WatermarkAnchorH | null;
    readonly anchorV?: WatermarkAnchorV | null;
    readonly inset?: number | null;
    /**
     * Per-axis overrides of `inset`, each a fraction of the smaller page
     * dimension like `inset` itself. Absent means "use `inset` for this axis",
     * which is every watermark — they inset equally on both and have no control
     * that would say otherwise.
     *
     * A Bates stamp does: its side margin and its header/footer margin are
     * separate production conventions and an operator can unlink them. That
     * lives here rather than in a second placement routine because the preview
     * and the burn-in worker both call THIS function, and a Bates-only copy of
     * the arithmetic is exactly the drift this module exists to prevent.
     */
    readonly insetH?: number | null;
    readonly insetV?: number | null;
    readonly customXPct?: number | null;
    readonly customYPct?: number | null;
  },
  drawnWidthPt: number,
  drawnHeightPt: number,
  pageWidthPt: number,
  pageHeightPt: number
): { cxPt: number; cyPt: number } {
  // A custom spot WINS, and it is stored page-relative rather than in points:
  // a definition is a default applied to documents of unknown size, so "62% of
  // the way across" survives a page this default has never seen, where "381pt"
  // would land somewhere else on every page size. The anchor and inset are left
  // on the record rather than cleared, so returning to a grid position restores
  // exactly what it was.
  if (isPct(wm.customXPct) && isPct(wm.customYPct)) {
    return {
      cxPt: wm.customXPct * pageWidthPt,
      cyPt: wm.customYPct * pageHeightPt,
    };
  }
  // Both axes measure against the SMALLER page dimension, including when they
  // carry different insets: the fraction has to mean the same physical distance
  // whichever edge it is measured from, or a stamp asked for equal margins
  // would sit further from the long edge than the short one.
  const minDimPt = Math.min(pageWidthPt, pageHeightPt);
  const insetHPt = clampWatermarkInset(wm.insetH ?? wm.inset) * minDimPt;
  const insetVPt = clampWatermarkInset(wm.insetV ?? wm.inset) * minDimPt;
  const h = wm.anchorH ?? 'center';
  const v = wm.anchorV ?? 'middle';
  const cxPt =
    h === 'left'
      ? insetHPt + drawnWidthPt / 2
      : h === 'right'
        ? pageWidthPt - insetHPt - drawnWidthPt / 2
        : pageWidthPt / 2;
  const cyPt =
    v === 'top'
      ? insetVPt + drawnHeightPt / 2
      : v === 'bottom'
        ? pageHeightPt - insetVPt - drawnHeightPt / 2
        : pageHeightPt / 2;
  return { cxPt, cyPt };
}

/** A usable page-relative coordinate: finite and inside the page. */
const isPct = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1;

/** Clamp a dragged position into the page. Both sides read it through this. */
export function clampWatermarkPct(pct: number | undefined | null): number {
  if (typeof pct !== 'number' || !Number.isFinite(pct)) {
    return 0.5;
  }
  return Math.max(0, Math.min(pct, 1));
}

/**
 * Coerce a stored inset into [0, 0.4].
 *
 * The ceiling is not decoration: an inset is applied to BOTH opposing edges by
 * the arithmetic above, so anything at or past 0.5 of the smaller dimension
 * crosses the page's midline and a "top" anchor lands below a "bottom" one.
 */
export function clampWatermarkInset(inset: number | undefined | null): number {
  if (typeof inset !== 'number' || !Number.isFinite(inset)) {
    return 0;
  }
  return Math.max(0, Math.min(inset, 0.4));
}

/** Drawn width of an image mark, in PDF points. */
export function imageWidthPt(
  scale: number | undefined | null,
  nativeWidthPt: number
): number {
  return clampWatermarkScale(scale) * nativeWidthPt;
}
